import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemButton,
  Stack,
  Chip,
  Typography,
  Divider,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import LabelIcon from '@mui/icons-material/Label';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { Client, Product, KeywordTag, CreateKeywordTagRequest, UpdateKeywordTagRequest } from '../types';
import BulkUpload from './BulkUpload';
import ProductCompetitors from './ProductCompetitors';

const productBulkUploadConfig = {
  title: 'Bulk Upload Products',
  endpoint: '/api/products/bulk-upload',
  templateColumns: ['name', 'asins'],
  templateName: 'product_upload_template.csv',
  successMessage: 'Products uploaded successfully',
  requiredColumns: ['name', 'asins'],
  optionalColumns: [],
  description: 'Upload a CSV file with product names and their ASINs. The ASINs column can contain multiple ASINs separated by commas or new lines.'
};

interface ProductManagerProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  clientName: string;
}

export default function ProductManager({
  open,
  onClose,
  clientId,
  clientName,
}: ProductManagerProps) {
  const [products, setProducts] = useState<Product[]>([]);



  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newProduct, setNewProduct] = useState({
    name: '',
    asin: '',
  });
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [asinConfirmOpen, setAsinConfirmOpen] = useState(false);
  const [asinToDelete, setAsinToDelete] = useState<{ productId: number; asinId: number } | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [keywordTags, setKeywordTags] = useState<KeywordTag[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [tagEditorOpen, setTagEditorOpen] = useState(false);
  const [currentTag, setCurrentTag] = useState<KeywordTag | null>(null);
  const [newTagData, setNewTagData] = useState<CreateKeywordTagRequest>({
    name: '',
    color: '#1976d2',
    keywords: []
  });
  const [tagKeywordsText, setTagKeywordsText] = useState('');
  const [tagConfirmOpen, setTagConfirmOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<KeywordTag | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open, clientId]);

  useEffect(() => {
    if (selectedProduct) {
      loadKeywordTags(selectedProduct.id);
    }
  }, [selectedProduct]);

  useEffect(() => {
    const filtered = products.filter((product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  const loadProducts = async () => {
    try {
      const data = await api.getProducts(clientId);
      const productsWithCompetitorCounts = await Promise.all(
        data.map(async (product) => {
          const competitorCount = await api.getProductCompetitorCount(product.id);
          return { ...product, competitorCount };
        })
      );
      setProducts(productsWithCompetitorCounts);
      setFilteredProducts(productsWithCompetitorCounts);

      // If a product is selected, update its state with the new data
      if (selectedProduct) {
        const updatedSelectedProduct = productsWithCompetitorCounts.find(p => p.id === selectedProduct.id);
        if (updatedSelectedProduct) {
          setSelectedProduct(updatedSelectedProduct);
        }
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };



  const loadKeywordTags = async (productId: number) => {
    try {
      setIsLoadingTags(true);
      const tags = await api.getProductKeywordTags(productId);
      setKeywordTags(tags);
    } catch (error) {
      console.error('Error loading keyword tags:', error);
    } finally {
      setIsLoadingTags(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      if (newProduct.name && newProduct.asin) {
        await api.createProduct(clientId, {
          name: newProduct.name,
          asins: newProduct.asin.trim(),
        });

        setNewProduct({ name: '', asin: '' });
        loadProducts();
      }
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleDeleteClick = (product: Product) => {
    setProductToDelete(product);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (productToDelete) {
      try {
        await api.deleteProduct(clientId, productToDelete.id);
        loadProducts();
      } catch (err) {
        console.error('Failed to delete product:', err);
      }
    }
    setConfirmOpen(false);
    setProductToDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setProductToDelete(null);
  };

  const handleDeleteAsin = async (productId: number, asinId: number) => {
    try {
      await api.deleteProductAsin(productId, asinId);
      loadProducts();
    } catch (error) {
      console.error('Error deleting ASIN:', error);
    }
  };

  const handleAddAsin = async (productId: number, asin: string) => {
    try {
      await api.addProductAsin(productId, asin.trim().toUpperCase());
      loadProducts();
      setSelectedProduct(null);
      setEditMode(false);
    } catch (error) {
      console.error('Error adding ASIN:', error);
    }
  };

  const handleEditProduct = async () => {
    if (!editingProduct) return;
    try {
      await api.updateProduct(clientId, editingProduct.id, {
        name: editingProduct.name,
        asins: editingProduct.asins.map((a) => a.asin).join(','),
      });
      loadProducts();
      setEditingProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleDeleteAsinClick = (productId: number, asinId: number) => {
    setAsinToDelete({ productId, asinId });
    setAsinConfirmOpen(true);
  };

  const handleConfirmDeleteAsin = async () => {
    if (asinToDelete) {
      try {
        await api.deleteProductAsin(asinToDelete.productId, asinToDelete.asinId);
        loadProducts(); // Reload the product list
      } catch (err) {
        console.error('Failed to delete ASIN:', err);
      }
    }
    setAsinConfirmOpen(false);
    setAsinToDelete(null);
  };

  const handleCancelDeleteAsin = () => {
    setAsinConfirmOpen(false);
    setAsinToDelete(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Products for {clientName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stack spacing={3}>
            {/* Add New Product Section */}
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Product Name"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="ASIN(s)"
                  value={newProduct.asin}
                  onChange={(e) => setNewProduct({ ...newProduct, asin: e.target.value })}
                  helperText="Separate multiple ASINs with commas"
                />
                <Button
                  variant="contained"
                  onClick={handleCreateProduct}
                  disabled={!newProduct.name || !newProduct.asin}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Stack>
              <Button
                variant="outlined"
                onClick={() => setIsBulkUploadOpen(true)}
                startIcon={<CloudUploadIcon />}
              >
                Bulk Upload
              </Button>
            </Box>

            {/* Search Products Section */}
            <TextField
              fullWidth
              label="Search Products"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              placeholder="Type to search products..."
            />

            {/* Products List */}
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
              {filteredProducts.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={searchQuery ? "No products found" : "No products yet"}
                    secondary={searchQuery ? "Try a different search term" : "Add your first product above"}
                  />
                </ListItem>
              ) : (
                filteredProducts.map((product) => (
                  <React.Fragment key={product.id}>
                    <ListItem
                      secondaryAction={
                        <>
                          <IconButton
                            edge="end"
                            aria-label="competitor analysis"
                            onClick={() => navigate(`/products/${product.id}/competitor-analysis`)}
                            sx={{ mr: 1 }}
                            title="Competitor Analysis"
                          >
                            <AssessmentIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={() => setEditingProduct(product)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteClick(product)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      }
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() => {
                          // Toggle selection if already selected, otherwise select
                          if (selectedProduct?.id === product.id) {
                            setSelectedProduct(null);
                            setEditMode(false);
                          } else {
                            setSelectedProduct(product);
                            setEditMode(true);
                          }
                        }}
                        selected={selectedProduct?.id === product.id}
                      >
                        <ListItemText
                          primary={product.name}
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {product.asins.map((asin: any) => (
                                <Chip
                                  key={asin.id}
                                  label={asin.asin}
                                  size="small"
                                  onDelete={() => handleDeleteAsinClick(product.id, asin.id)}
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              ))}
                              <Chip
                                icon={<EditIcon fontSize="small" />}
                                label={`${product.competitorCount} Competitors`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mb: 1 }}
                              />
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    
                    {/* Render ASIN/Competitor UI directly below the selected product */}
                    {selectedProduct?.id === product.id && editMode && (
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'background.paper', 
                        borderRadius: 0, 
                        borderLeft: '1px solid #e0e0e0',
                        borderRight: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                        boxShadow: 'inset 0 3px 5px rgba(0,0,0,0.05)',
                        ml: 0, 
                        mr: 0, 
                        mb: 0,
                        pt: 3
                      }}>
                        <Tabs 
                          value={activeTab} 
                          onChange={(e, value) => setActiveTab(value)}
                          sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            mb: 2
                          }}
                        >
                          <Tab label="ASINs" />
                          <Tab label={`Competitors (${selectedProduct.competitorCount})`} />
                          <Tab label="KEYWORD TAGS" />
                        </Tabs>
                        {activeTab === 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Add ASIN to {selectedProduct.name}
                            </Typography>
                            <Stack direction="row" spacing={2}>
                              <TextField
                                fullWidth
                                label="New ASIN"
                                value={newProduct.asin}
                                onChange={(e) => setNewProduct({ ...newProduct, asin: e.target.value })}
                                size="small"
                              />
                              <Button
                                variant="contained"
                                onClick={() => handleAddAsin(selectedProduct.id, newProduct.asin)}
                                disabled={!newProduct.asin}
                                size="medium"
                              >
                                Add ASIN
                              </Button>
                            </Stack>
                          </Box>
                        )}
                        {activeTab === 1 && (
                          <Box sx={{ mt: 2 }}>
                            <ProductCompetitors productId={selectedProduct.id} onUpdate={loadProducts} />
                          </Box>
                        )}
                        {activeTab === 2 && (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Keyword Tags for {selectedProduct.name}
                              </Typography>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  setCurrentTag(null);
                                  setNewTagData({
                                    name: '',
                                    color: '#1976d2',
                                    keywords: []
                                  });
                                  setTagKeywordsText('');
                                  setTagEditorOpen(true);
                                  setHasUnsavedChanges(false);
                                }}
                                size="small"
                              >
                                New Tag
                              </Button>
                            </Box>
                            
                            {isLoadingTags ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : keywordTags.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No keyword tags yet. Create your first tag to organize keywords.
                              </Typography>
                            ) : (
                              <List>
                                {keywordTags.map((tag) => (
                                  <ListItem
                                    key={tag.id}
                                    secondaryAction={
                                      <Box>
                                        <IconButton
                                          edge="end"
                                          aria-label="edit"
                                          onClick={() => {
                                            setCurrentTag(tag);
                                            setNewTagData({
                                              name: tag.name,
                                              color: tag.color,
                                              keywords: tag.keywords.map(k => k.keyword)
                                            });
                                            setTagKeywordsText(tag.keywords.map(k => k.keyword).join('\n'));
                                            setTagEditorOpen(true);
                                            setHasUnsavedChanges(false);
                                          }}
                                          sx={{ mr: 1 }}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          edge="end"
                                          aria-label="delete"
                                          onClick={() => {
                                            setTagToDelete(tag);
                                            setTagConfirmOpen(true);
                                          }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Box>
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Box
                                            sx={{
                                              width: 16,
                                              height: 16,
                                              borderRadius: '50%',
                                              bgcolor: tag.color,
                                              mr: 1
                                            }}
                                          />
                                          {tag.name}
                                        </Box>
                                      }
                                      secondary={`${tag.keyword_count} keywords`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>

            {/* The Edit Product ASINs section has been moved inline with each product */}
          </Stack>
        </Box>

        <BulkUpload
          open={isBulkUploadOpen}
          onClose={() => {
            setIsBulkUploadOpen(false);
            loadProducts();
          }}
          onSuccess={() => {
            setIsBulkUploadOpen(false);
            loadProducts();
          }}
          config={productBulkUploadConfig}
          clientId={clientId}
        />
      </DialogContent>
      {/* Edit Product Dialog */}
      <Dialog open={!!editingProduct} onClose={() => setEditingProduct(null)}>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Product Name"
            fullWidth
            value={editingProduct?.name || ''}
            onChange={(e) =>
              setEditingProduct(
                editingProduct
                  ? { ...editingProduct, name: e.target.value }
                  : null
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingProduct(null)}>Cancel</Button>
          <Button onClick={handleEditProduct} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Delete Dialog */}
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this product?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Delete ASIN Dialog */}
      <Dialog open={asinConfirmOpen} onClose={handleCancelDeleteAsin}>
        <DialogTitle>Confirm ASIN Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this ASIN?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteAsin} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteAsin} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag Editor Dialog */}
      <Dialog 
        open={tagEditorOpen} 
        onClose={() => {
          if (hasUnsavedChanges) {
            // Ask for confirmation before closing
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
              setTagEditorOpen(false);
            }
          } else {
            setTagEditorOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{currentTag ? 'Edit Tag' : 'Create New Tag'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Tag Name"
              value={newTagData.name}
              onChange={(e) => {
                setNewTagData({ ...newTagData, name: e.target.value });
                setHasUnsavedChanges(true);
              }}
              required
              error={newTagData.name.trim() === ''}
              helperText={newTagData.name.trim() === '' ? 'Tag name is required' : ''}
              inputProps={{ maxLength: 50 }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <Select
                value={newTagData.color || '#1976d2'}
                label="Color"
                onChange={(e) => {
                  setNewTagData({ ...newTagData, color: e.target.value });
                  setHasUnsavedChanges(true);
                }}
              >
                <MenuItem value="#1976d2">Blue</MenuItem>
                <MenuItem value="#2e7d32">Green</MenuItem>
                <MenuItem value="#d32f2f">Red</MenuItem>
                <MenuItem value="#ed6c02">Orange</MenuItem>
                <MenuItem value="#9c27b0">Purple</MenuItem>
                <MenuItem value="#00796b">Teal</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="subtitle2">Keywords (one per line)</Typography>
            <TextareaAutosize
              minRows={5}
              style={{ 
                width: '100%', 
                padding: '8px', 
                fontFamily: 'inherit',
                fontSize: '14px',
                borderColor: '#ccc',
                borderRadius: '4px'
              }}
              value={tagKeywordsText}
              onChange={(e) => {
                setTagKeywordsText(e.target.value);
                setHasUnsavedChanges(true);
              }}
              placeholder="Enter keywords, one per line"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (hasUnsavedChanges) {
              // Ask for confirmation before closing
              if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                setTagEditorOpen(false);
              }
            } else {
              setTagEditorOpen(false);
            }
          }}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (newTagData.name.trim() === '') {
                return; // Don't save if name is empty
              }
              
              // Process keywords from textarea
              const keywords = tagKeywordsText
                .split('\n')
                .map(k => k.trim())
                .filter(k => k !== '');
              
              const tagData = {
                ...newTagData,
                keywords
              };
              
              try {
                if (currentTag) {
                  // Update existing tag
                  await api.updateKeywordTag(currentTag.id, tagData);
                } else {
                  // Create new tag
                  await api.createKeywordTag(selectedProduct!.id, tagData);
                }
                
                // Reload tags
                loadKeywordTags(selectedProduct!.id);
                setTagEditorOpen(false);
                setHasUnsavedChanges(false);
              } catch (error) {
                console.error('Error saving tag:', error);
                alert('Failed to save tag. Please try again.');
              }
            }} 
            color="primary"
            variant="contained"
            disabled={newTagData.name.trim() === ''}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirm Delete Tag Dialog */}
      <Dialog open={tagConfirmOpen} onClose={() => setTagConfirmOpen(false)}>
        <DialogTitle>Confirm Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the tag "{tagToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (tagToDelete) {
                try {
                  await api.deleteKeywordTag(tagToDelete.id);
                  loadKeywordTags(selectedProduct!.id);
                  setTagConfirmOpen(false);
                  setTagToDelete(null);
                } catch (error) {
                  console.error('Error deleting tag:', error);
                  alert('Failed to delete tag. Please try again.');
                }
              }
            }} 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
